/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */
/**
 * Styled components for CreateInquiryForm
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Bababnov
 * @version 1.22.0
 * @since 1.0.0
 */

import styled from 'styled-components';

import { icons } from '@assets/index';
import { LabelStyled } from '@components/common/Input/styled/InputStyled';
import { TextContainerStyled } from '@components/common/TextBlock/styled/TextBlockStyled';
import { colors, fonts } from '@theme/index';

export const CreateInquiryFormContainerStyled = styled.div.attrs({
    className: 'create-inquiry__container',
})`
    & .select__select-list {
        max-width: unset;
    }
    & .loader-backdrop {
        top: 110px;
    }
    & .loader__frame {
        transform: translate(-50%,-50%);
    }
`;

export const FormItemContainerStyled = styled.div.attrs({
    className: 'create-inquiry__item',
})`
    position: relative;
    &:not(:last-child) {
        margin-bottom: 24px;
    }
    &:only-of-type {
        margin-bottom: 2px;
    }

    &.required label::after {
       content: " *";
       font-size: inherit;
       color: ${colors.createInquiryError};
    }

    & label {
        ${fonts.textMainBold};
    }

    & textarea {
        padding: 12px 20px;
        min-height: 96px;
        box-sizing: border-box;
        ${fonts.textMain};
        outline: none;
    }
    & .drop-down-filter,
    & .drop-down-filter__filter-button,
    & .select-container {
        background-color: ${colors.createInquirySelectBg};
        & .drop-down-filter__select-placeholder,
        & .arrow-tooltip__text {
            font-size: 15px;
        }
        & > .arrow-tooltip__text {
            line-height: 48px;
        }
    }
    & ${LabelStyled} {
        display: flex;
    }
`;
export const CreateInquiryFieldContainer = styled.div.attrs({
    className: 'create-inquiry__body',
})`
    padding: 24px;
    max-height: 325px;
    overflow-y: auto;
    background-color: ${colors.createInquiryFieldBg};

    & .create-inquiry__footnote {
        ${fonts.textSmall};

    }
    & .create-inquiry__footnote::before {
        color: ${colors.createInquiryError};
        content: "* ";
    }
`;
export const ErrorMessage = styled.div.attrs({
    className: 'create-inquiry__error',
})`
    ${fonts.textSmall};
    color: ${colors.createInquiryError}
    padding-top: 4px;
`;

export const InquiryAttachButtonStyled = styled.label.attrs({
    className: 'create-inquiry__attach',
})`
    width: 140px;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    padding-top: 12px;

    & input {
        display: none;
    }

    & ${TextContainerStyled} {
        border-bottom-width: 1px;
        border-bottom-style: dashed;
        &:hover {
            border-bottom-style: solid;
        }
    }
`;
export const InquiryAttachItemStyled = styled.div.attrs({
    className: 'create-inquiry__attach-file',
})`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    padding-top: 12px;
`;
export const InquiryAttachIconStyled = styled.div.attrs({
    className: 'create-inquiry__attach-icon',
})`
    position: relative;
    width: 20px;
    height: 24px;
    margin-right: 14px;
    border: 2px solid ${colors.createInquiryAttachBorder};

    &:before {
        content: '';
        position: absolute;
        top: -2px;
        right: -2px;
        width: 7px;
        height: 7px;
        border-width: 0 0 2px 2px;
        border-style: solid;
        border-color: ${colors.createInquiryAttachBorder};
        background-color: white;
    }
    &:after {
        content: '';
        position: absolute;
        top: -4px;
        right: -1px;
        width: 3px;
        height: 10px;
        transform: rotate(-45deg);
        border-left: 2px solid ${colors.createInquiryAttachBorder};
        outline: none;
    }
`;
export const InquiryAttachTypeStyled = styled.div.attrs({
    className: 'create-inquiry__attach-type',
})`
    position: absolute;
    top: 10px;
    left: -5px;
    font-size: 12px;
    font-weight: bold;
    line-height: 1;
    background-color: ${colors.createInquiryAttachBg};
`;
export const InquiryAttachNameStyled = styled.div.attrs({
    className: 'create-inquiry__attach-name',
})`
    ${fonts.textLink};
`;
export const InquiryAttachCloseIconStyled = styled.div.attrs({
    className: 'create-inquiry__attach-close',
})`
    width: 17px;
    height: 17px;
    margin-left: 5px;
    background: url(${icons.cancelMiddleIcon}) no-repeat;
    background-size: cover;
    cursor: pointer;
`;

export const FieldRequiredStyled = styled.sup`
    margin-left: 4px;
    margin-top: -4px;
    font-size: 15px;
    color: ${colors.createInquiryError};
`

export const FormTooltipStyled = styled.div`
    margin-left: 7px;
    width: 16px;
    height: 16px;
    align-self: center;
    background: url(${icons.attentionInvertBlackIcon}) no-repeat;
`
